import { Box, Heading, VStack } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';

const LONG_LINE_CHARS = 120;

function normalizeToData(value: unknown): unknown {
  if (value === undefined) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }
  return value;
}

/** Split physical lines that are still very long so the editor can wrap or paint them cheaply. */
function breakLongSegments(lines: string[], maxLen: number): string[] {
  return lines.flatMap((line) => {
    if (line.length <= maxLen) return [line];
    const chunks: string[] = [];
    for (let i = 0; i < line.length; i += maxLen) {
      chunks.push(line.slice(i, i + maxLen));
    }
    return chunks;
  });
}

/**
 * Pretty view for audit JSON: real newlines inside string values are shown as separate lines
 * (not as \\n in one mega-line). Long unbroken strings are chunked for display.
 * Output is JSON-like but not always valid JSON when strings are expanded.
 */
function formatJsonReadable(value: unknown, indent: number): string {
  const pad = '  '.repeat(indent);
  const pad1 = '  '.repeat(indent + 1);

  if (value === null) return 'null';
  if (typeof value === 'boolean' || typeof value === 'number') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') {
    const needsBreak = value.includes('\n') || value.length > LONG_LINE_CHARS;
    if (!needsBreak) return JSON.stringify(value);
    const lines = breakLongSegments(value.split('\n'), LONG_LINE_CHARS);
    return `\n${pad1}${lines.join(`\n${pad1}`)}`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const parts = value.map((item, i) => {
      const formatted = formatJsonReadable(item, indent + 1);
      const body = formatted.startsWith('\n') ? formatted : ` ${formatted}`;
      return `${pad1}[${i}]:${body}`;
    });
    return `[\n${parts.join(',\n')}\n${pad}]`;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length === 0) return '{}';
    const parts = keys.map((k) => {
      const v = o[k];
      const formatted = formatJsonReadable(v, indent + 1);
      const key = JSON.stringify(k);
      if (formatted.startsWith('\n')) {
        return `${pad1}${key}:${formatted}`;
      }
      return `${pad1}${key}: ${formatted}`;
    });
    return `{\n${parts.join(',\n')}\n${pad}}`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function jsonToEditorText(value: unknown): string {
  const data = normalizeToData(value);
  try {
    return formatJsonReadable(data, 0);
  } catch {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }
}

interface JsonReadonlyBlockProps {
  title: string;
  value: unknown;
  height?: string;
}

export function JsonReadonlyBlock({
  title,
  value,
  height = 'min(50vh, 480px)',
}: JsonReadonlyBlockProps) {
  const text = jsonToEditorText(value);

  return (
    <VStack gap={2} align="stretch">
      <Heading size="sm" color="fg">
        {title}
      </Heading>
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderColor="border"
        overflow="hidden"
      >
        <Editor
          height={height}
          defaultLanguage="plaintext"
          value={text}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            wordWrap: 'bounded',
            wordWrapColumn: LONG_LINE_CHARS,
            wrappingStrategy: 'advanced',
            scrollBeyondLastLine: false,
            folding: true,
            lineNumbers: 'on',
            renderLineHighlight: 'none',
          }}
        />
      </Box>
    </VStack>
  );
}
